/**
 * components/forms/FormsTable.tsx
 *
 * Unified sortable table for form templates.
 * Supports both regular user view and admin view (with "Utilisateur" column + toggle visibility).
 * Updated 2026-03-16: removed Attribuer/Désattribuer/Dupliquer actions; simplified admin menu to
 *   Aperçu (draft included for admin) + Rendre visible/Masquer + Modifier + Supprimer.
 *   Renamed "Attribué à" column to "Utilisateur".
 * Updated 2026-04-16: re-added Attribuer action for admin; added is_assigned and assignment_count
 *   fields; removed "Personnalisé" badge (all forms are now admin-assigned); restricted edit/delete on assigned forms.
 * Updated 2026-04-16: added has_unread_assignment field; blue dot indicator on unread assigned forms;
 *   fixed trailing separator in dropdown for assigned forms; onMarkRead callback wired to preview/send.
 */

import { useSortableTable } from '../../hooks/useSortableTable';
import Badge from '../ui/Badge';
import DropdownMenu, { DropdownItem } from '../ui/DropdownMenu';
import { MoreVertical, Eye, Pencil, Trash2, Send, ToggleRight, ToggleLeft, UserPlus, Users } from 'lucide-react';
import { formatRelativeDate } from '../../utils/dateUtils';

export interface TemplateWithMeta {
  id: string;
  name: string;
  status: 'active' | 'draft';
  created_by: string | null;
  user_id: string;
  is_visible_to_user: boolean;
  updated_at: string;
  last_sent_at: string | null;
  owner_email: string | null;
  is_assigned: boolean;
  assignment_count: number;
  has_unread_assignment?: boolean;
}

interface FormsTableProps {
  templates: TemplateWithMeta[];
  isAdmin: boolean;
  onSend: (template: TemplateWithMeta) => void;
  onPreview: (templateId: string) => void;
  onEdit: (template: TemplateWithMeta) => void;
  onDelete: (templateId: string) => void;
  onToggleVisibility: (template: TemplateWithMeta) => void;
  onAssign: (template: TemplateWithMeta) => void;
  onMarkRead?: (templateId: string) => void;
  sendingId: string | null;
}

export default function FormsTable({
  templates,
  isAdmin,
  onSend,
  onPreview,
  onEdit,
  onDelete,
  onToggleVisibility,
  onAssign,
  onMarkRead,
  sendingId,
}: FormsTableProps) {
  const { sortedData, toggleSort, renderSortIcon } = useSortableTable({
    data: templates,
    initialSortKey: 'updated_at',
    initialSortDirection: 'desc',
  });

  const getActions = (template: TemplateWithMeta) => {
    const actions: DropdownItem[] = [];

    const canPreview = template.status === 'active' || isAdmin;

    if (canPreview) {
      actions.push({
        label: 'Aperçu',
        icon: <Eye className="w-4 h-4" />,
        onClick: () => {
          onMarkRead?.(template.id);
          onPreview(template.id);
        },
      });
    }

    if (!isAdmin && template.status === 'active') {
      actions.push({
        label: sendingId === template.id ? 'Chargement...' : 'Envoyer',
        icon: <Send className="w-4 h-4" />,
        onClick: () => {
          onMarkRead?.(template.id);
          onSend(template);
        },
        disabled: sendingId === template.id,
      });
    }

    if (isAdmin && template.status === 'active') {
      actions.push({
        label: template.is_visible_to_user ? "Masquer pour l'utilisateur" : 'Rendre visible',
        icon: template.is_visible_to_user
          ? <ToggleLeft className="w-4 h-4" />
          : <ToggleRight className="w-4 h-4" />,
        onClick: () => onToggleVisibility(template),
      });
    }

    if (isAdmin) {
      actions.push({
        label: 'Attribuer',
        icon: <UserPlus className="w-4 h-4" />,
        onClick: () => onAssign(template),
      });
    }

    const canEdit = isAdmin || !template.is_assigned;
    const canDelete = isAdmin || !template.is_assigned;

    if ((canPreview || (!isAdmin && template.status === 'active')) && (canEdit || canDelete)) {
      actions.push({ type: 'separator' as const });
    }

    if (canEdit) {
      actions.push({
        label: 'Modifier',
        icon: <Pencil className="w-4 h-4" />,
        onClick: () => onEdit(template),
      });
    }

    if (canDelete) {
      actions.push({ type: 'separator' as const });
      actions.push({
        label: 'Supprimer',
        icon: <Trash2 className="w-4 h-4" />,
        onClick: () => onDelete(template.id),
        variant: 'danger' as const,
      });
    }

    return actions;
  };

  const thBtn = 'flex items-center gap-1.5 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-700 transition-colors';

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white shadow-soft overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]">
        <thead>
          <tr className="border-b border-neutral-100 bg-neutral-50/60">
            <th className="text-left px-5 py-3.5">
              <button onClick={() => toggleSort('name')} className={thBtn}>
                Nom {renderSortIcon('name')}
              </button>
            </th>
            <th className="text-left px-4 py-3.5 hidden sm:table-cell">
              <button onClick={() => toggleSort('status')} className={thBtn}>
                Statut {renderSortIcon('status')}
              </button>
            </th>
            <th className="text-left px-4 py-3.5 hidden md:table-cell">
              <button onClick={() => toggleSort('updated_at')} className={thBtn}>
                Modifié {renderSortIcon('updated_at')}
              </button>
            </th>
            <th className="text-left px-4 py-3.5 hidden lg:table-cell">
              <button onClick={() => toggleSort('last_sent_at')} className={thBtn}>
                Dernier envoi {renderSortIcon('last_sent_at')}
              </button>
            </th>
            {isAdmin && (
              <th className="text-left px-4 py-3.5 hidden xl:table-cell">
                <button onClick={() => toggleSort('owner_email')} className={thBtn}>
                  Utilisateur {renderSortIcon('owner_email')}
                </button>
              </th>
            )}
            <th className="w-14 px-4 py-3.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-50">
          {sortedData.map((template) => (
            <tr key={template.id} className="hover:bg-neutral-50/50 transition-colors">
              <td className="px-5 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-2 font-medium text-neutral-900 leading-snug">
                    {template.is_assigned && template.has_unread_assignment && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"
                        title="Nouveau formulaire attribué"
                        aria-label="Non lu"
                      />
                    )}
                    {template.name}
                  </span>
                  {isAdmin && template.assignment_count > 0 && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full">
                      <Users className="w-3 h-3" />
                      {template.assignment_count}
                    </span>
                  )}
                  {template.status === 'draft' && (
                    <span className="sm:hidden">
                      <Badge variant="warning" size="sm">Brouillon</Badge>
                    </span>
                  )}
                  {template.status === 'active' && !template.is_visible_to_user && isAdmin && (
                    <span className="sm:hidden">
                      <Badge variant="info" size="sm">Prêt</Badge>
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 hidden sm:table-cell">
                {template.status === 'draft' ? (
                  <Badge variant="warning" size="sm">Brouillon</Badge>
                ) : !template.is_visible_to_user && isAdmin ? (
                  <Badge variant="info" size="sm">Prêt</Badge>
                ) : (
                  <Badge variant="success" size="sm">Actif</Badge>
                )}
              </td>
              <td className="px-4 py-4 hidden md:table-cell text-neutral-500 text-xs whitespace-nowrap">
                {formatRelativeDate(template.updated_at)}
              </td>
              <td className="px-4 py-4 hidden lg:table-cell text-neutral-500 text-xs whitespace-nowrap">
                {formatRelativeDate(template.last_sent_at)}
              </td>
              {isAdmin && (
                <td className="px-4 py-4 hidden xl:table-cell">
                  <span className="text-xs block max-w-[160px] truncate">
                    {template.owner_email ? (
                      <span className="text-neutral-700">{template.owner_email}</span>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </span>
                </td>
              )}
              <td className="px-4 py-4">
                <DropdownMenu
                  trigger={
                    <button className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors">
                      <MoreVertical className="w-4 h-4 text-neutral-500" />
                    </button>
                  }
                  items={getActions(template)}
                  align="right"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
